import { z } from 'zod';
import { DocumentCategorySchema } from './documents.schemas';
import { refersTo } from '../common/refers-to';

/**
 * Document-analysis contract (IMM-366.2). A `Dokumentanalyse` is an async,
 * per-field-confidence extraction of one property document by a vision LLM,
 * persisted separately from the source document (never mutating it). This module
 * defines the wire shapes for triggering an analysis and reading its latest
 * result; the LLM specifics stay server-side behind the extraction gateway.
 */

// The async lifecycle of one analysis, mirroring the run engine's states: a fresh
// analysis is `PENDING`; the job flips it `ANALYZING`; it ends in one terminal
// state — `COMPLETED` (result present), `NO_DATA` (no usable content, reason
// present) or `FAILED` (the run errored / no vision model, reason present).
export const DocumentAnalysisStatusSchema = z.enum(['PENDING', 'ANALYZING', 'COMPLETED', 'NO_DATA', 'FAILED']);

export type DocumentAnalysisStatus = z.infer<typeof DocumentAnalysisStatusSchema>;

// Per-field confidence the model attaches to each extracted value. The
// low-confidence "bitte prüfen" rendering is a client concern (IMM-366.3).
export const AnalysisConfidenceSchema = z.enum(['HIGH', 'MEDIUM', 'LOW']);

export type AnalysisConfidence = z.infer<typeof AnalysisConfidenceSchema>;

// One extracted field: the raw value (any JSON, null when "nicht ermittelt"), the
// model's confidence, and the manual-correction attributes (IMM-366.4). At
// extraction time `corrected` is `false` and the attribution props are absent;
// a manual correction sets `corrected: true`, keeps the model's reading in
// `originalValue`, and records who + when. All stored in the `result` jsonb — no
// migration.
export const AnalysisFieldSchema = z.object({
  value: z.unknown(),
  confidence: AnalysisConfidenceSchema,
  corrected: z.boolean(),
  originalValue: z.unknown().optional(),
  correctedByName: z.string().optional(),
  correctedByMemberId: z.uuid().optional(),
  correctedAt: z.iso.datetime().optional(),
  // 1-based page the value was read from; absent on a result stored before it was kept.
  page: z.number().int().positive().nullable().optional(),
});

// Body for a manual field correction (IMM-366.4): the property-contract field
// key to correct and the member's replacement value (a text reading).
export const CorrectDocumentAnalysisFieldBodySchema = z
  .object({
    fieldKey: z.string().min(1),
    value: z.string().trim().min(1),
  })
  .strict();

export type CorrectDocumentAnalysisFieldBody = z.infer<typeof CorrectDocumentAnalysisFieldBodySchema>;

export type AnalysisField = z.infer<typeof AnalysisFieldSchema>;

// Body for applying extracted values onto the Objekt (IMM-382): the analysis the
// member reviewed plus the field keys they explicitly checked in the confirm
// modal. Only these are written — an unchecked key is never applied, and a checked
// one that conflicts with an existing property value is the member's deliberate
// overwrite. `analysisId` pins the write to the exact analysis the member saw: a
// re-run that supersedes it before they confirm must not silently write values
// they never reviewed. `fieldKeys` is de-duplicated so a repeated key cannot
// double-count in the outcome, and bounded so the body can't smuggle an
// unbounded list. The cap comfortably exceeds every mappable Objekt field.
export const ApplyDocumentAnalysisBodySchema = z
  .object({
    analysisId: z.uuid(),
    fieldKeys: z
      .array(z.string().min(1))
      .min(1)
      .max(64)
      .transform((keys) => [...new Set(keys)]),
  })
  .strict();

export type ApplyDocumentAnalysisBody = z.infer<typeof ApplyDocumentAnalysisBodySchema>;

// Why a requested field key was not written. `not-extracted` — the key is absent
// from the analysis (or was read as null); `unsupported-field` — the key maps to
// no Objekt field; `invalid-value` — the value could not be coerced to the field's
// real type (e.g. a manual correction that isn't a number); `section-incomplete` —
// the target section is all-or-nothing and the Objekt holds no such section to
// merge onto, so the write would fabricate the missing scalars;
// `section-not-applicable` — the section does not apply to this property (a rental
// price on a SALE Objekt, or vice versa); `analysis-superseded` — a re-run replaced
// the analysis the member reviewed before they confirmed, so nothing is written and
// every requested key carries this reason. The last two are property-level promotion
// reasons (IMM-500.4): `conflicting` — the key's live facts disagree, so the layer
// offers no value to write; `below-confidence-floor` — no supporting fact clears the
// suggestion confidence floor. `certificate-not-gmodg` — a Primärenergiebedarf
// reading onto an Objekt whose Ausweis, after the merge, is not a GModG-Ausweis;
// `energy-class-off-scale` — a class reading that is off the Objekt's scale after
// the merge (the stored class is kept).
export const ApplySkipReasonSchema = z.enum([
  'not-extracted',
  'unsupported-field',
  'invalid-value',
  'section-incomplete',
  'section-not-applicable',
  'analysis-superseded',
  'conflicting',
  'below-confidence-floor',
  'certificate-not-gmodg',
  'energy-class-off-scale',
]);

export type ApplySkipReason = z.infer<typeof ApplySkipReasonSchema>;

export const ApplySkippedFieldSchema = z.object({
  fieldKey: z.string(),
  reason: ApplySkipReasonSchema,
});

export type ApplySkippedField = z.infer<typeof ApplySkippedFieldSchema>;

// The outcome of one apply: which keys reached the Objekt and which did not (with
// the reason). Partial success is normal and not an error — the caller reports it.
// The updated Objekt itself is not echoed; the client re-reads it.
export const ApplyDocumentAnalysisResponseSchema = z.object({
  appliedFieldKeys: z.array(z.string()),
  skippedFields: z.array(ApplySkippedFieldSchema),
});

export type ApplyDocumentAnalysisResponse = z.infer<typeof ApplyDocumentAnalysisResponseSchema>;

// The COMPLETED result shape: a map of property-contract field keys to their
// extracted `{ value, confidence, corrected }`. Keys are constrained to the
// property field contract at build time so the shape can't drift from the Objekt.
export const DocumentAnalysisResultSchema = z.object({
  fields: z.record(z.string(), AnalysisFieldSchema),
});

export type DocumentAnalysisResult = z.infer<typeof DocumentAnalysisResultSchema>;

// The latest analysis for a document (or an in-flight one). `result` is present
// only for `COMPLETED`; `failureReason` for `NO_DATA` / `FAILED`; `model` records
// which vision model produced a completed result.
export const DocumentAnalysisResponseSchema = z.object({
  id: z.uuid(),
  propertyId: refersTo('property', z.uuid()),
  documentId: refersTo('document', z.uuid()),
  documentCategory: DocumentCategorySchema,
  status: DocumentAnalysisStatusSchema,
  failureReason: z.string().nullable(),
  result: DocumentAnalysisResultSchema.nullable(),
  model: z.string().nullable(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
});

export type DocumentAnalysisResponse = z.infer<typeof DocumentAnalysisResponseSchema>;

// Path params addressing the analysis of one document under a property.
export const DocumentAnalysisPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  documentId: z.uuid(),
});

// One row of the per-document status list (IMM-366.3): the latest analysis
// status for a document, so the Dokumente list can show each document's analyse
// state at a glance without a request per row. A document that has never been
// analysed is simply absent from the list.
export const DocumentAnalysisSummarySchema = z.object({
  documentId: refersTo('document', z.uuid()),
  status: DocumentAnalysisStatusSchema,
  completedAt: z.iso.datetime().nullable(),
});

export type DocumentAnalysisSummary = z.infer<typeof DocumentAnalysisSummarySchema>;

export const DocumentAnalysisListResponseSchema = z.object({
  analyses: z.array(DocumentAnalysisSummarySchema),
});

export type DocumentAnalysisListResponse = z.infer<typeof DocumentAnalysisListResponseSchema>;
